// src/utils/hooks/useAssignee.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { assignClientsToMembers } from "../api";
import { getMembers } from "../api/members";
import type { Member } from "../../types/members";
import { useAuthStore } from "../stores/authStore";

interface AssignClientPayload {
  memberId: string | null;
  clientId: string;
}

export const useAssignee = (clientId?: string) => {
  const queryClient = useQueryClient();
  const { role } = useAuthStore();

  interface MembersResponse {
    businessStaff: Member[];
    counts: any;
    message: string;
  }

  // Fetch members - only enabled for non-client roles
  const {
    data: members,
    isLoading: isLoadingMembers,
    isError: isErrorMembers,
    error: membersError,
  } = useQuery<MembersResponse, Error>({
    queryKey: ["members"],
    queryFn: async () => {
      const response = await getMembers();
      return response;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    enabled: role !== undefined && role !== "client", // Only fetch for tax pros
  });

  // Mutation to assign client to member
  const assignMutation = useMutation<void, Error, AssignClientPayload>({
    mutationFn: async (payload) => {
      await assignClientsToMembers(payload);
    },
    onSuccess: () => {
      // Invalidate relevant queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["client", clientId] });
      queryClient.invalidateQueries({ queryKey: ["members"] });
    },
  });

  // Function to assign client to a member
  const assignClient = async (memberId: string | null) => {
    if (!clientId) {
      throw new Error("Client ID is required");
    }
    await assignMutation.mutateAsync({ memberId, clientId });
  };

  return {
    members: members?.businessStaff,
    isLoadingMembers,
    isErrorMembers,
    membersError,
    assignClient,
    isAssigning: assignMutation.isPending,
    assignError: assignMutation.error,
  };
};
